import { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { authHeaders } from '../listPageStyles';
import {
  Section, Grid, Icon, SubModal,
  FieldText, FieldSelect, FieldDate, FieldTextarea, FieldCustom, FieldToggle,
} from '../detail/DetailShell';
import { COUNTRIES, getProvinces, getDistricts } from '../../utils/trGeo';
import { formatTrPhone } from '../../utils/phone';

const API = '/api/RepresentativeIdentity';

// Entegratör sözlüğü — backend'deki EDocumentIntegrators ile AYNI anahtarları konuşur.
// Gerçek sağlayıcı henüz yok (NullEDocumentProvider); anahtar seçilse bile gönderim, sağlayıcı
// yazılana kadar kuyrukta bekler. Bu dürüst bir bekleyiştir: ekran "gönderilemedi" YALANI söylemez.
const INTEGRATOR_OPTIONS = [
  { value: '', label: 'Seçiniz' },
  { value: 'unidox', label: 'UniDox' },
  { value: 'sutasgib', label: 'Sütaş GİB' },
];

const ASSET_META = {
  signature: { label: 'İmza Resmi', icon: 'edit', hint: 'Belge alt bilgisi' },
  logo: { label: 'Dist Logo', icon: 'box', hint: 'Belge üst bilgisi' },
  stamp: { label: 'Kaşe Resmi', icon: 'check', hint: 'Belge kaşesi' },
};

const EMPTY = {
  distributionChannelVendorId: '', commercialTitle: '', taxNumber: '', taxOffice: '',
  country: 'Türkiye', city: '', district: '', address: '', phone1: '', phone2: '', contactPerson: '',
  trackingCode: '', distributorTitle: '', integrator: '', integratorUsername: '',
  integratorPassword: '', institutionCode: '', authorizedUserCode: '', taxpayerSince: '',
  senderLabels: '', receiverLabels: '', eIrsaliyeSenderLabels: '', eIrsaliyeReceiverLabels: '',
  eFaturaWsUrl: '', eArsivWsUrl: '', eIrsaliyeWsUrl: '',
  authorizationWsUrl: '', queryWsUrl: '', eIrsaliyeQueryWsUrl: '',
  xsltUuid: '', eFaturaXsltUuid: '',
  eFaturaEnabled: false, eArsivEnabled: false, eIrsaliyeEnabled: false,
  eArsivOfflineAllowed: false, eArsivPrintAsUnprinted: false,
};

const pick = (o, ...ks) => { if (!o) return undefined; for (const k of ks) if (o[k] != null) return o[k]; return undefined; };
const unwrap = (p) => pick(p, 'Data', 'data') ?? p;
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

/**
 * "Firma ve E-Belge Ayarları" sekmesi (spec §3, §4).
 *
 * ── ALT TÜCCARDA SEKME GİZLENMEZ, KİLİTLENİR ──────────────────────────────────────────────────
 * Gizlemek kolaydı; ama alt tüccarın kullanıcısı "hangi VKN ile, hangi kaşeyle basıyorum" sorusunu
 * SORAMAZ hâle gelirdi. Sekme görünür, alanlar salt okunur, üstte miras bandı ve ana karta giden
 * bir link durur (spec §5.3).
 *
 * ── ŞİFRE FORMDA DÜZ METİN DEĞİL ──────────────────────────────────────────────────────────────
 * Sunucu şifreyi ASLA döndürmez; yalnız "tanımlı mı" (hasIntegratorPassword) der. Alan boş
 * bırakılırsa şifreye DOKUNULMAZ (null gönderilir) — kullanıcı sadece adresi düzeltirken GİB
 * şifresinin uçmaması için. "Göster" düğmesi ayrı, yetkiye kapılı bir uca gider ve o çağrı
 * denetime yazılır.
 */
export const CompanyProfileTab = forwardRef(function CompanyProfileTab(
  { repId, vendors = [], onToast, onReadOnlyChange, refreshKey = 0 }, ref,
) {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({ readOnly: false, isNew: true, ownerRepresentativeId: null, ownerRepresentativeName: '', hasPassword: false });
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tried, setTried] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [deleteAsset, setDeleteAsset] = useState(null);
  const [preview, setPreview] = useState(null);

  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const load = useCallback(async () => {
    if (!repId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/company-profile/${repId}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      const d = unwrap(body);
      if (!res.ok || !d) { setLoading(false); return; }

      setMeta({
        readOnly: !!d.readOnly,
        isNew: !!d.isNew,
        ownerRepresentativeId: d.ownerRepresentativeId,
        ownerRepresentativeName: d.ownerRepresentativeName || '',
        hasPassword: !!d.hasIntegratorPassword,
      });
      if (onReadOnlyChange) onReadOnlyChange(!!d.readOnly);

      setForm({
        ...EMPTY,
        ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, d[k] ?? EMPTY[k]])),
        // Şifre sunucudan GELMEZ — form alanı daima boş başlar (boş = dokunma).
        integratorPassword: '',
        taxpayerSince: toDateInput(d.taxpayerSince),
        country: d.country || 'Türkiye',
      });
      setAssets(d.assets || []);
    } catch {
      onToast?.('error', 'Firma ve e-belge ayarları alınamadı');
    } finally {
      setLoading(false);
    }
    // refreshKey: Genel Bilgiler'deki "Ana Ticari Kimlik" anahtarıyla bağ koparıldığında profil
    // SIFIRLANIR (boş form). Tazelenmezse ekran, artık kullanılmayan ananın VKN'sini göstermeye
    // devam ederdi — kullanıcı da belge kesebileceğini sanırdı.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repId, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const provinces = useMemo(() => getProvinces(form.country), [form.country]);
  const districts = useMemo(() => getDistricts(form.country, form.city), [form.country, form.city]);

  const opts = (arr, ph = 'Seçiniz') => [{ value: '', label: ph }, ...arr.map((v) => ({ value: v, label: v }))];

  const vendorOptions = useMemo(() => [
    { value: '', label: 'Seçiniz' },
    ...vendors.map((v) => ({
      value: String(v.id ?? v.Id),
      label: `${v.textId ?? v.TextId ?? ''} - ${v.name ?? v.Name ?? ''}`.trim().replace(/^- /, ''),
    })),
  ], [vendors]);

  /** Ana kart Kaydet'e bastığında çağrılır (parent → useImperativeHandle). */
  const save = useCallback(async () => {
    if (meta.readOnly) return true;   // alt tüccar: kaydedilecek bir şey yok, sessizce geç
    setTried(true);

    const payload = {
      ...form,
      taxpayerSince: form.taxpayerSince || null,
      distributionChannelVendorId: form.distributionChannelVendorId || null,
      // Boş = DOKUNMA (null). Kullanıcı şifreyi değiştirmediyse mevcut şifre korunur.
      integratorPassword: form.integratorPassword ? form.integratorPassword : null,
    };

    const res = await fetch(`${API}/company-profile/${repId}`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Firma ve e-belge ayarları kaydedilemedi');

    await load();
    return true;
  }, [form, meta.readOnly, repId, load]);

  useImperativeHandle(ref, () => ({ save, isReadOnly: () => meta.readOnly }), [save, meta.readOnly]);

  const revealPassword = async () => {
    try {
      const res = await fetch(`${API}/integrator-password/${repId}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Şifre görüntülenemedi');
      const val = unwrap(body);
      setRevealed(val || '');
      if (!val) onToast?.('info', 'Şifre tanımlanmamış.');
    } catch (err) {
      onToast?.('error', err.message);
    }
  };

  const uploadAsset = (assetType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      // 2 MB: bir kaşe/imza görseli için fazlasıyla yeterli. Sınır olmadan, base64 kolonu bir
      // telefon fotoğrafıyla şişer ve her belge basımında o yükü taşırdık.
      if (file.size > 2 * 1024 * 1024) { onToast?.('error', 'Görsel 2 MB’ı aşamaz.'); return; }
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await fetch(`${API}/edocument-asset/${repId}`, {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ assetType, imageData: reader.result, fileName: file.name }),
          });
          const body = await res.json().catch(() => null);
          if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Görsel yüklenemedi');
          onToast?.('success', `${ASSET_META[assetType].label} yüklendi`);
          await load();
        } catch (err) { onToast?.('error', err.message); }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const removeAsset = async () => {
    const a = deleteAsset;
    setDeleteAsset(null);
    try {
      const res = await fetch(`${API}/edocument-asset/${a.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Görsel silinemedi');
      onToast?.('success', `${ASSET_META[a.assetType]?.label || 'Görsel'} silindi`);
      await load();
    } catch (err) { onToast?.('error', err.message); }
  };

  if (loading) return <div className="dps-form"><Section icon="box" title="Yükleniyor…" /></div>;

  const ro = meta.readOnly;
  const assetOf = (t) => assets.find((a) => a.assetType === t);

  return (
    <div className="dps-form">
      {ro && (
        <div className="dps-inherit">
          <Icon name="info" size={16} />
          <div>
            Bu ayarlar <strong>{meta.ownerRepresentativeName || 'ana tüccar'}</strong>’dan gelir ve salt
            okunurdur. Değişiklik için{' '}
            <a onClick={() => navigate(`/representatives/${meta.ownerRepresentativeId}?edit=1`)}>ana karta gidin ↗</a>
          </div>
        </div>
      )}

      <Section
        icon="box" title="Firma Tanımları"
        subtitle="Ticari kimlik bilgileri — e-belgelerde satıcı olarak basılır"
      >
        <Grid cols={2}>
          <FieldSelect form={form} set={set} k="distributionChannelVendorId" label="Distribütör"
                       options={vendorOptions} disabled={ro} />
          <FieldText form={form} set={set} tried={tried} k="commercialTitle" label="Ticari Ünvan" req readOnly={ro} />
          <FieldText form={form} set={set} tried={tried} k="taxNumber" label="VN (Vergi No / VKN)" req mono readOnly={ro}
                     hint="10 haneli vergi no veya 11 haneli TC kimlik no" />
          <FieldText form={form} set={set} tried={tried} k="taxOffice" label="VD (Vergi Dairesi)" req readOnly={ro} />
          <FieldSelect form={form} set={set} tried={tried} k="country" label="Ülke" req options={opts(COUNTRIES)}
                       disabled={ro} onChange={(v) => setForm((c) => ({ ...c, country: v, city: '', district: '' }))} />
          <FieldSelect form={form} set={set} tried={tried} k="city" label="İl" req options={opts(provinces)}
                       disabled={ro} onChange={(v) => setForm((c) => ({ ...c, city: v, district: '' }))} />
          <FieldSelect form={form} set={set} tried={tried} k="district" label="İlçe" req options={opts(districts)}
                       disabled={ro || !form.city} />
          <FieldText form={form} set={set} tried={tried} k="phone1" label="Telefon 1" req readOnly={ro} format={formatTrPhone} />
          <FieldTextarea form={form} set={set} k="address" label="Adres" rows={2} span readOnly={ro} />
          <FieldText form={form} set={set} k="phone2" label="Telefon 2" readOnly={ro} format={formatTrPhone} />
          <FieldText form={form} set={set} k="contactPerson" label="İlgili Kişi" readOnly={ro} />
        </Grid>
      </Section>

      <Section icon="box" title="E-Belge Ayarları" subtitle="Entegratör bağlantısı ve GİB kimliği">
        <Grid cols={2}>
          <FieldText form={form} set={set} k="trackingCode" label="Takip Kodu" readOnly={ro} />
          <FieldText form={form} set={set} k="distributorTitle" label="Distribütör Ünvan" readOnly={ro} />
          <FieldSelect form={form} set={set} k="integrator" label="Entegratör" options={INTEGRATOR_OPTIONS} disabled={ro} />
          <FieldText form={form} set={set} tried={tried} k="integratorUsername" label="Kullanıcı Adı"
                     req={!!form.integrator} readOnly={ro} />

          <FieldCustom label="Şifre">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type={revealed != null ? 'text' : 'password'}
                className="mono"
                value={revealed != null ? revealed : form.integratorPassword}
                readOnly={ro || revealed != null}
                placeholder={meta.hasPassword ? '••••••••  (tanımlı — değiştirmek için yazın)' : 'Tanımlanmadı'}
                onChange={(e) => set('integratorPassword', e.target.value)}
                style={{ flex: 1 }}
              />
              {meta.hasPassword && !ro && (
                <button type="button" className="dps-btn ghost"
                        onClick={() => (revealed != null ? setRevealed(null) : revealPassword())}>
                  {revealed != null ? 'Gizle' : 'Göster'}
                </button>
              )}
            </div>
          </FieldCustom>

          <FieldText form={form} set={set} k="institutionCode" label="Kurum Kodu" readOnly={ro} />
          <FieldText form={form} set={set} k="authorizedUserCode" label="Yetkili Kullanıcı Kodu" readOnly={ro} />
          <FieldDate form={form} set={set} k="taxpayerSince" label="Mükellef Olma Tarihi" readOnly={ro} />
        </Grid>

        <div style={{ height: 6 }} />
        <Grid cols={2}>
          <FieldText form={form} set={set} k="senderLabels" label="Gönderici Etiketleri" span readOnly={ro} />
          <FieldText form={form} set={set} k="receiverLabels" label="Alıcı Etiketleri" span readOnly={ro} />
          <FieldText form={form} set={set} k="eIrsaliyeSenderLabels" label="E-İrsaliye Gönderici Etiketleri" span readOnly={ro} />
          <FieldText form={form} set={set} k="eIrsaliyeReceiverLabels" label="E-İrsaliye Alıcı Etiketleri" span readOnly={ro} />
        </Grid>

        {/* Belge tipi anahtarları — web servis adreslerinin ÜSTÜNDE duruyorlar, çünkü hangi adresin
            gerekli olduğunu BUNLAR belirler. Altta olsalardı kullanıcı önce adresleri doldurup sonra
            "aslında bu belgeyi kesmiyorum" derdi. */}
        <div className="dps-sec-head" style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--dps-line)' }}>
          <div className="dps-sec-tt">
            <h3 style={{ fontSize: 14 }}>Kesilebilen Belgeler</h3>
            <p>Kapalı bir belge tipinin web servis adresi istenmez</p>
          </div>
        </div>
        <Grid cols={2}>
          <FieldToggle form={form} set={set} k="eFaturaEnabled" label="E-Fatura" disabled={ro}
                       hint="Bu ticari kimlik e-Fatura kesebilir" />
          <FieldToggle form={form} set={set} k="eArsivEnabled" label="E-Arşiv" disabled={ro}
                       hint="Bu ticari kimlik e-Arşiv kesebilir" />
          <FieldToggle form={form} set={set} k="eIrsaliyeEnabled" label="E-İrsaliye" disabled={ro}
                       hint="Sütaş adına (nama) e-İrsaliye düzenleyebilir" />
          <FieldToggle form={form} set={set} k="eArsivOfflineAllowed" label="E-Arşiv offline kaydedilebilsin" disabled={ro}
                       hint="Bağlantısız e-Arşiv üretimi" />
          <FieldToggle form={form} set={set} k="eArsivPrintAsUnprinted" label="E-Arşiv “yazdırılmadı” olarak kaydedilsin" disabled={ro}
                       hint="Basım durumu varsayılanı" span />
        </Grid>

        <div style={{ height: 14 }} />
        <Grid cols={2}>
          {/* Kapalı belgenin adresi SOLUK gösterilir (spec §4.5) — kesilemeyecek bir belgenin
              adresini istemek kafa karıştırır. */}
          <FieldText form={form} set={set} tried={tried} k="eFaturaWsUrl" label="E-Fatura Web Servis Adresi"
                     req={form.eFaturaEnabled} span mono readOnly={ro || !form.eFaturaEnabled} />
          <FieldText form={form} set={set} tried={tried} k="eArsivWsUrl" label="E-Arşiv Web Servis Adresi"
                     req={form.eArsivEnabled} span mono readOnly={ro || !form.eArsivEnabled} />
          <FieldText form={form} set={set} tried={tried} k="eIrsaliyeWsUrl" label="E-İrsaliye Web Servis Adresi"
                     req={form.eIrsaliyeEnabled} span mono readOnly={ro || !form.eIrsaliyeEnabled} />
          <FieldText form={form} set={set} k="authorizationWsUrl" label="Yetkilendirme Web Servis Adresi"
                     span mono readOnly={ro} />
          <FieldText form={form} set={set} tried={tried} k="queryWsUrl" label="Sorgulama Web Servis Adresi"
                     req={form.eFaturaEnabled || form.eArsivEnabled || form.eIrsaliyeEnabled}
                     span mono readOnly={ro} />
          <FieldText form={form} set={set} tried={tried} k="eIrsaliyeQueryWsUrl" label="E-İrsaliye Sorg. Web Serv. Adresi"
                     req={form.eIrsaliyeEnabled} span mono readOnly={ro || !form.eIrsaliyeEnabled} />
          <FieldText form={form} set={set} k="xsltUuid" label="XSLT UUID" mono readOnly={ro} />
          <FieldText form={form} set={set} k="eFaturaXsltUuid" label="E-Fatura XSLT UUID" mono readOnly={ro} />
        </Grid>
      </Section>

      <Section
        icon="box" title="Belge Tasarım Görselleri"
        subtitle="E-belge dizaynlarında kullanılır — PNG/JPG, en fazla 2 MB"
        note={meta.isNew ? 'Görsel yüklemek için önce firma ve e-belge ayarlarını kaydedin.' : undefined}
      >
        <div className="dps-assets">
          {Object.entries(ASSET_META).map(([type, m]) => {
            const a = assetOf(type);
            return (
              <div className="dps-asset" key={type}>
                <div className="dps-asset-ph">
                  {a ? <img src={a.previewUrl} alt={m.label} /> : <Icon name={m.icon} size={26} />}
                </div>
                <p className="dps-asset-nm">{m.label}</p>
                <div className="dps-asset-btns">
                  <button type="button" className="dps-btn ghost sm" disabled={!a}
                          onClick={() => setPreview({ ...a, label: m.label })}>Önizle</button>
                  {!ro && (
                    <>
                      <button type="button" className="dps-btn danger sm" disabled={!a}
                              onClick={() => setDeleteAsset(a)}>Sil</button>
                      <button type="button" className="dps-btn primary sm" disabled={meta.isNew}
                              onClick={() => uploadAsset(type)}>Yükle</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {preview && (
        <SubModal title={preview.label} maxWidth={640} onClose={() => setPreview(null)}
                  footer={<button className="dps-btn ghost" onClick={() => setPreview(null)}>Kapat</button>}>
          {/* Gerçek en-boy oranı korunur (spec §4.6) — çarpıtılmış bir kaşe önizlemesi, basımda
              nasıl görüneceği hakkında yanlış fikir verirdi. */}
          <img src={preview.previewUrl} alt={preview.label}
               style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
          {preview.fileName && <p style={{ textAlign: 'center', color: 'var(--dps-ink-3)', fontSize: 12 }}>{preview.fileName}</p>}
        </SubModal>
      )}

      {deleteAsset && (
        <SubModal title="Görseli Sil" maxWidth={440} onClose={() => setDeleteAsset(null)}
                  footer={(
                    <>
                      <button className="dps-btn ghost" onClick={() => setDeleteAsset(null)}>İptal</button>
                      <button className="dps-btn danger" onClick={removeAsset}>Evet, Sil</button>
                    </>
                  )}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>{ASSET_META[deleteAsset.assetType]?.label}</strong> silinecek. Bu görseli kullanan
            e-belge dizaynlarında alan boş kalır. Emin misiniz?
          </p>
        </SubModal>
      )}
    </div>
  );
});

export default CompanyProfileTab;
